/* eslint-disable no-await-in-loop, no-restricted-syntax */
import { getDocumentOcr } from 'services/inference/ocrService';
import type { TextractBlock } from 'services/inference/ocrService';
import type { ImportPage } from 'services/imports/fileToPages';

export type OcrLine = {
  index: string;
  id: string;
  text: string;
  confidence: number;
  bbox: { left: number; top: number; width: number; height: number };
};

export type OcrPageStatus = 'done' | 'error' | 'skipped' | 'cancelled';

export type OcrTable = {
  rows: string[][];
};

export type OcrPageResult = {
  pageId: string;
  pageNumber: number;
  status: OcrPageStatus;
  lines: OcrLine[];
  tables: OcrTable[];
  error?: string;
};

export type RunOcrOptions = {
  onProgress?: (completed: number, total: number, result: OcrPageResult) => void;
  signal?: AbortSignal;
  tables?: boolean;
};

const toErrorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

const resultFor = (
  page: ImportPage,
  status: OcrPageStatus,
  lines: OcrLine[] = [],
  tables: OcrTable[] = [],
  error?: string,
): OcrPageResult => ({
  pageId: page.id,
  pageNumber: page.pageNumber,
  status,
  lines,
  tables,
  ...(error === undefined ? {} : { error })
});

const isUsableLine = (block: TextractBlock): boolean => (
  block?.BlockType === 'LINE' && typeof block.Text === 'string' && !!block.Geometry?.BoundingBox
);

/** Keeps LINE blocks only (response order) and numbers them L1..Ln after filtering. */
const blocksToLines = (blocks: TextractBlock[]): OcrLine[] => blocks
  .filter(isUsableLine)
  .map((block, position) => {
    const { Left, Top, Width, Height } = block.Geometry.BoundingBox;
    return {
      index: `L${position + 1}`,
      id: block.Id,
      text: block.Text ?? '',
      confidence: block.Confidence ?? 0,
      bbox: { left: Left, top: Top, width: Width, height: Height }
    };
  });

const CHILD_RELATIONSHIP = 'CHILD';
const GRID_BLOCK_TYPES = new Set(['CELL', 'MERGED_CELL']);

const childIds = (block: TextractBlock): string[] => (block.Relationships ?? [])
  .filter((relationship) => relationship.Type === CHILD_RELATIONSHIP)
  .flatMap((relationship) => relationship.Ids);

const childBlocks = (block: TextractBlock, byId: ReadonlyMap<string, TextractBlock>): TextractBlock[] => childIds(block)
  .map((id) => byId.get(id))
  .filter((child): child is TextractBlock => child !== undefined);

const textOf = (block: TextractBlock, byId: ReadonlyMap<string, TextractBlock>): string => {
  if (block.BlockType === 'WORD') return block.Text ?? '';
  return childBlocks(block, byId)
    .map((child) => textOf(child, byId))
    .filter((text) => text !== '')
    .join(' ');
};

const placedCells = (table: TextractBlock, byId: ReadonlyMap<string, TextractBlock>): TextractBlock[] => {
  const children = childBlocks(table, byId);
  const mergedInner = new Set(
    children
      .filter((child) => child.BlockType === 'MERGED_CELL')
      .flatMap((merged) => childIds(merged)),
  );
  return children.filter((child) => (
    GRID_BLOCK_TYPES.has(child.BlockType)
    && child.RowIndex !== undefined
    && child.ColumnIndex !== undefined
    && !(child.BlockType === 'CELL' && mergedInner.has(child.Id))
  ));
};

const blocksToTables = (blocks: TextractBlock[]): OcrTable[] => {
  const byId = new Map(blocks.map((block) => [block.Id, block]));
  return blocks
    .filter((block) => block.BlockType === 'TABLE')
    .map((table) => {
      const cells = placedCells(table, byId).map((cell) => ({
        row: cell.RowIndex ?? 0,
        column: cell.ColumnIndex ?? 0,
        rowSpan: cell.RowSpan ?? 1,
        columnSpan: cell.ColumnSpan ?? 1,
        text: textOf(cell, byId),
      }));
      const rowCount = cells.reduce((max, cell) => Math.max(max, cell.row + cell.rowSpan - 1), 0);
      const columnCount = cells.reduce((max, cell) => Math.max(max, cell.column + cell.columnSpan - 1), 0);
      const rows: string[][] = Array.from({ length: rowCount }, () => Array.from({ length: columnCount }, () => ''));
      cells.forEach((cell) => {
        rows[cell.row - 1][cell.column - 1] = cell.text;
      });
      return { rows };
    });
};

const ABORTED = 'aborted';

/**
 * Stops waiting for `work` as soon as `signal` aborts. The request itself is not
 * cancelled (the GraphQL client does not take an AbortSignal), so its late
 * response is ignored.
 */
const untilAborted = <T>(work: Promise<T>, signal?: AbortSignal): Promise<T> => {
  if (!signal) return work;
  return new Promise<T>((resolve, reject) => {
    const onAbort = (): void => reject(new Error(ABORTED));
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener('abort', onAbort, { once: true });
    work
      .then(resolve, reject)
      .finally(() => signal.removeEventListener('abort', onAbort));
  });
};

const ocrPage = async (page: ImportPage, tables: boolean, signal?: AbortSignal): Promise<OcrPageResult> => {
  try {
    const response = await untilAborted(getDocumentOcr(page.imageDataUrl, { passThrough: true, tables }), signal);
    if (!Array.isArray(response?.Blocks)) {
      return resultFor(page, 'error', [], [], 'OCR response has no Blocks');
    }
    return resultFor(page, 'done', blocksToLines(response.Blocks), tables ? blocksToTables(response.Blocks) : []);
  } catch (error) {
    // An aborted request is a cancellation, not an OCR failure.
    if (signal?.aborted) return resultFor(page, 'cancelled');
    return resultFor(page, 'error', [], [], toErrorMessage(error));
  }
};

/**
 * Runs OCR sequentially, one page at a time. Always returns one result per
 * input page, in order. Aborting stops waiting for the in-flight page at once; the rest
 * are reported as `cancelled`. Results are meant for screen state only.
 */
export const runOcrForPages = async (
  pages: readonly ImportPage[],
  options: RunOcrOptions = {}
): Promise<OcrPageResult[]> => {
  const { onProgress, signal, tables = false } = options;
  const total = pages.length;
  const results: OcrPageResult[] = [];

  for (const page of pages) {
    let result: OcrPageResult;
    if (signal?.aborted) {
      result = resultFor(page, 'cancelled');
    } else if (page.failed) {
      result = resultFor(page, 'skipped');
    } else {
      result = await ocrPage(page, tables, signal);
    }
    results.push(result);
    onProgress?.(results.length, total, result);
  }

  return results;
};
